import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import { FieldLabel, formFieldSx } from '../../../styles/formStyles';
import { createEditCancelButtonSx, createEditPrimaryButtonSx } from '../../../styles/pageFormStyles';
import { getCountries } from '../climate/climate.geo';
import {
    MSP_SEASONS,
    MSP_UNIT,
    saveCropAlias,
    saveMspPrice,
    validateCropAlias,
    validateMsp,
} from './marketReference.service';

const dialogPaperSx = { sx: { borderRadius: '24px' } };

const hintSx = { fontSize: 13, color: '#625B71', mt: 1 };

const emptyMsp = {
    countryId: '',
    cropId: null,
    cropName: '',
    seasonCode: '',
    marketingYear: '',
    mspValue: '',
    unit: MSP_UNIT,
    notifiedAt: '',
    notes: '',
};

/** India first when it exists, since MSP is an Indian instrument; otherwise the first country. */
function defaultCountryId(list) {
    return list.find((country) => country.name.toLowerCase() === 'india')?.id || list[0]?.id || '';
}

/**
 * Add or edit one MSP price. `price` with an id edits that row; without an id it is a prefill
 * (from the Coverage tab) for a new row.
 */
export function MspPriceModal({ open, price, existing = [], onClose, onSaved }) {
    const [draft, setDraft] = useState(emptyMsp);
    const [issues, setIssues] = useState({});
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [countries, setCountries] = useState([]);
    const [countryNote, setCountryNote] = useState('');

    const editing = Boolean(price?.id);

    useEffect(() => {
        if (!open) return;
        setDraft({ ...emptyMsp, ...(price || {}) });
        setIssues({});
        setError('');
        setSaving(false);
    }, [open, price]);

    useEffect(() => {
        if (!open) return undefined;
        let active = true;
        (async () => {
            const { countries: list, failed } = await getCountries();
            if (!active) return;
            setCountries(list);
            setCountryNote(failed ? 'Could not load the country list.' : list.length === 0 ? 'No active countries are configured.' : '');
            setDraft((prev) => (prev.countryId ? prev : { ...prev, countryId: defaultCountryId(list) }));
        })();
        return () => {
            active = false;
        };
    }, [open]);

    const set = (field) => (event) => {
        setDraft((prev) => ({ ...prev, [field]: event.target.value }));
        setIssues((prev) => ({ ...prev, [field]: undefined }));
    };

    const handleSave = async () => {
        const found = validateMsp(draft, existing);
        setIssues(found);
        if (Object.keys(found).length) return;

        setSaving(true);
        const result = await saveMspPrice(draft);
        setSaving(false);

        if (!result.success) {
            if (result.issues) setIssues(result.issues);
            setError(result.error.message);
            return;
        }
        onSaved(result.data);
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={dialogPaperSx}>
            <DialogTitle sx={{ fontSize: 22, fontWeight: 700, color: '#1C1B1F', pb: 1 }}>
                {editing ? 'Edit MSP price' : 'Add MSP price'}
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ color: '#625B71', fontSize: 15, mb: 3 }}>
                    The government's Minimum Support Price for a crop. Used for the price-gap signal and the "sale below
                    floor" warning, only for farmers in this country. The newest marketing year wins.
                </Typography>

                {error ? <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{error}</Alert> : null}

                <Box sx={{ mb: 2.5 }}>
                    <FieldLabel label="Country" required />
                    <TextField
                        select
                        fullWidth
                        value={draft.countryId}
                        onChange={set('countryId')}
                        error={Boolean(issues.countryId)}
                        helperText={issues.countryId || countryNote}
                        sx={formFieldSx}
                    >
                        {countries.map((country) => (
                            <MenuItem key={country.id} value={country.id}>{country.name}</MenuItem>
                        ))}
                    </TextField>
                </Box>

                <Box sx={{ mb: 2.5 }}>
                    <FieldLabel label="Crop" required />
                    <TextField
                        fullWidth
                        value={draft.cropName}
                        onChange={set('cropName')}
                        placeholder="Paddy"
                        error={Boolean(issues.cropName)}
                        helperText={issues.cropName}
                        sx={formFieldSx}
                    />
                    <Typography sx={hintSx}>
                        Use the crop's main name. Other names farmers use (Dhan, Rice) are mapped to it under Crop
                        names.
                    </Typography>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
                    <Box>
                        <FieldLabel label="Season" />
                        <TextField
                            select
                            fullWidth
                            value={draft.seasonCode || ''}
                            onChange={set('seasonCode')}
                            error={Boolean(issues.seasonCode)}
                            helperText={issues.seasonCode}
                            sx={formFieldSx}
                        >
                            {MSP_SEASONS.map((season) => (
                                <MenuItem key={season.code || 'any'} value={season.code}>{season.label}</MenuItem>
                            ))}
                        </TextField>
                    </Box>
                    <Box>
                        <FieldLabel label="Marketing year" />
                        <TextField
                            fullWidth
                            value={draft.marketingYear || ''}
                            onChange={set('marketingYear')}
                            placeholder="2026-27"
                            error={Boolean(issues.marketingYear)}
                            helperText={issues.marketingYear}
                            sx={formFieldSx}
                        />
                    </Box>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
                    <Box>
                        <FieldLabel label="MSP (₹ per quintal)" required />
                        <TextField
                            fullWidth
                            type="number"
                            value={draft.mspValue}
                            onChange={set('mspValue')}
                            inputProps={{ min: 0, step: 1 }}
                            placeholder="2369"
                            error={Boolean(issues.mspValue)}
                            helperText={issues.mspValue}
                            sx={formFieldSx}
                        />
                    </Box>
                    <Box>
                        <FieldLabel label="Notified on" />
                        <TextField
                            fullWidth
                            type="date"
                            value={draft.notifiedAt || ''}
                            onChange={set('notifiedAt')}
                            sx={formFieldSx}
                        />
                    </Box>
                </Box>

                <Box>
                    <FieldLabel label="Notes" />
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        value={draft.notes || ''}
                        onChange={set('notes')}
                        placeholder="Variety (e.g. Common), PIB release and who signed it off"
                        sx={formFieldSx}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, pt: 1, justifyContent: 'space-between' }}>
                <Button onClick={onClose} sx={createEditCancelButtonSx}>Cancel</Button>
                <Button variant="contained" disabled={saving} onClick={handleSave} sx={createEditPrimaryButtonSx}>
                    {saving ? 'Saving…' : 'Save price'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

const emptyAlias = { name: '', canonicalName: '', agmarknetCommodity: '', notes: '' };

/** Add or edit one crop name mapping; an `alias` without an id is a prefill for a new row. */
export function CropAliasModal({ open, alias, existing = [], onClose, onSaved }) {
    const [draft, setDraft] = useState(emptyAlias);
    const [issues, setIssues] = useState({});
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    const editing = Boolean(alias?.id);

    useEffect(() => {
        if (!open) return;
        setDraft({ ...emptyAlias, ...(alias || {}) });
        setIssues({});
        setError('');
        setSaving(false);
    }, [open, alias]);

    const set = (field) => (event) => {
        setDraft((prev) => ({ ...prev, [field]: event.target.value }));
        setIssues((prev) => ({ ...prev, [field]: undefined }));
    };

    const handleSave = async () => {
        const found = validateCropAlias(draft, existing);
        setIssues(found);
        if (Object.keys(found).length) return;

        setSaving(true);
        const result = await saveCropAlias(draft);
        setSaving(false);

        if (!result.success) {
            if (result.issues) setIssues(result.issues);
            setError(result.error.message);
            return;
        }
        onSaved(result.data);
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={dialogPaperSx}>
            <DialogTitle sx={{ fontSize: 22, fontWeight: 700, color: '#1C1B1F', pb: 1 }}>
                {editing ? 'Edit crop name' : 'Add crop name'}
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ color: '#625B71', fontSize: 15, mb: 3 }}>
                    Tells eK Score which crop a name means, so MSP prices, soil moisture thresholds and mandi prices
                    are found whatever name a DARE typed.
                </Typography>

                {error ? <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{error}</Alert> : null}

                <Box sx={{ mb: 2.5 }}>
                    <FieldLabel label="Name used" required />
                    <TextField
                        fullWidth
                        value={draft.name}
                        onChange={set('name')}
                        placeholder="Dhan"
                        error={Boolean(issues.name)}
                        helperText={issues.name}
                        sx={formFieldSx}
                    />
                    <Typography sx={hintSx}>Matched ignoring upper/lower case and spaces at the ends.</Typography>
                </Box>

                <Box sx={{ mb: 2.5 }}>
                    <FieldLabel label="Means crop" required />
                    <TextField
                        fullWidth
                        value={draft.canonicalName}
                        onChange={set('canonicalName')}
                        placeholder="Paddy"
                        error={Boolean(issues.canonicalName)}
                        helperText={issues.canonicalName}
                        sx={formFieldSx}
                    />
                    <Typography sx={hintSx}>Write it exactly as in MSP prices and soil moisture thresholds.</Typography>
                </Box>

                <Box sx={{ mb: 2.5 }}>
                    <FieldLabel label="Mandi commodity (data.gov.in)" />
                    <TextField
                        fullWidth
                        value={draft.agmarknetCommodity || ''}
                        onChange={set('agmarknetCommodity')}
                        placeholder="Paddy(Common)"
                        error={Boolean(issues.agmarknetCommodity)}
                        helperText={issues.agmarknetCommodity}
                        sx={formFieldSx}
                    />
                    <Typography sx={hintSx}>
                        The exact Agmarknet commodity spelling. Leave empty outside India or when unsure — prices are
                        then looked up under the crop name.
                    </Typography>
                </Box>

                <Box>
                    <FieldLabel label="Notes" />
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        value={draft.notes || ''}
                        onChange={set('notes')}
                        placeholder="Language or region the name comes from"
                        sx={formFieldSx}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, pt: 1, justifyContent: 'space-between' }}>
                <Button onClick={onClose} sx={createEditCancelButtonSx}>Cancel</Button>
                <Button variant="contained" disabled={saving} onClick={handleSave} sx={createEditPrimaryButtonSx}>
                    {saving ? 'Saving…' : 'Save name'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
